import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { basicInfoContainerStyle } from '../../../../styles/commonStyles';
import { NUMERATION_SCHEMA_ENDPOINT } from '../../../../consts';
import { NumerationSchema } from '../../../../types/numerationSchemas';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import CustomCircularProgress from '../../../../components/ui/customCircularProgress/customCircularProgress.component';
import { formatDateTime } from '../../../../utils';
import { TENANT_ROUTES } from '../../tenantConfig.routes';

export const TenantNumerationSchemaDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useCRUD(NUMERATION_SCHEMA_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [schema, setSchema] = useState<NumerationSchema | null>(null);
  const canViewSchemas = useHasPermission("settlement.view_numerationschema");

  useEffect(() => {
    const fetchSchema = async () => {
      if (id) {
        const result = await get(id);
        if (result?.success && result?.data) {
          setSchema(result.data);
          setOverride(TENANT_ROUTES.numerationSchema(id), result.data.name || '');
        } else {
          navigate(TENANT_ROUTES.numbering);
        }
      }
    };

    const handleSchemaUpdated = () => {
      fetchSchema();
    };

    window.addEventListener('numerationSchemasUpdated', handleSchemaUpdated);
    fetchSchema();
    return () => {
      window.removeEventListener('numerationSchemasUpdated', handleSchemaUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canViewSchemas) {
    return null;
  }

  if (!schema) {
    return <CustomCircularProgress />;
  }

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {schema.name}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle1">
            <strong>{t("Name")}:</strong> {schema.name || "-"}
          </Typography>
          <Typography variant="subtitle1">
            <strong>{t("Template")}:</strong> {schema.template || "-"}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Metadata Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Metadata")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Created At")}:</strong> {formatDateTime(schema.created_at)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Updated At")}:</strong> {formatDateTime(schema.updated_at)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
